import React, { useMemo, useState } from 'react';
import { Check, Lock, RotateCcw, ShieldCheck, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { usePermissions } from '../../context/PermissionsContext.tsx';
import {
  CONFIGURABLE_ROLES,
  DEFAULT_PERMISSION_MAP,
  PERMISSIONS,
  PermissionKey,
  ROLE_LABELS,
  isLockedCell,
} from '../../../shared/permissions.ts';

// Roles & Access: one row per power, one column per role. An administrator ticks or unticks a box and
// it is saved and applied straight away (the server enforces it, every open screen follows).

export const RolesPermissionsView: React.FC = () => {
  const { token, user } = useAuth();
  const { permissions, can, applyMap } = usePermissions();
  const [savingCell, setSavingCell] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [message, setMessage] = useState<{ text: string; error?: boolean } | null>(null);

  const canEdit = can('roles.manage');

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token || ''}`,
    ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
  };

  const flash = (text: string, error = false) => {
    setMessage({ text, error });
    setTimeout(() => setMessage((m) => (m && m.text === text ? null : m)), 4000);
  };

  const groups = useMemo(() => {
    const byGroup = new Map<string, typeof PERMISSIONS>();
    for (const def of PERMISSIONS) {
      byGroup.set(def.group, [...(byGroup.get(def.group) || []), def]);
    }
    return Array.from(byGroup.entries());
  }, []);

  const customised = useMemo(
    () =>
      PERMISSIONS.some((def) => {
        const now = [...permissions[def.key]].sort().join(',');
        const base = [...DEFAULT_PERMISSION_MAP[def.key]].sort().join(',');
        return now !== base;
      }),
    [permissions]
  );

  const toggle = async (key: PermissionKey, role: string, allowed: boolean) => {
    if (!canEdit) return;
    const cell = `${key}:${role}`;
    setSavingCell(cell);
    try {
      const res = await fetch('/api/permissions', {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ permission: key, role, allowed }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.permissions) {
        applyMap(body.permissions);
        const def = PERMISSIONS.find((p) => p.key === key);
        flash(`${ROLE_LABELS[role] || role} ${allowed ? 'can now' : 'can no longer'}: ${def?.label}`);
      } else {
        flash(body.error || `Could not save the change (HTTP ${res.status}).`, true);
      }
    } catch {
      flash('Could not save the change: the server could not be reached.', true);
    } finally {
      setSavingCell(null);
    }
  };

  const resetAll = async () => {
    if (!canEdit) return;
    if (!window.confirm('Put every role back to its original permissions?')) return;
    setIsResetting(true);
    try {
      const res = await fetch('/api/permissions/reset', { method: 'POST', headers: authHeaders });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.permissions) {
        applyMap(body.permissions);
        flash('All roles are back to their original permissions.');
      } else {
        flash(body.error || `Could not reset (HTTP ${res.status}).`, true);
      }
    } catch {
      flash('Could not reset: the server could not be reached.', true);
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-2xl bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 shadow-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-surface-900 dark:text-surface-100 flex items-center space-x-2">
              <ShieldCheck className="w-4 h-4 text-brand-500" />
              <span>Roles & Access</span>
              {customised && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                  Customised
                </span>
              )}
            </h3>
            <p className="text-xs text-surface-500 dark:text-surface-400 mt-1 max-w-2xl">
              Choose what each role is allowed to do. A change is saved and applied right away, for everyone who is
              signed in. Administrators always keep every permission.
            </p>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={resetAll}
              disabled={isResetting || !customised}
              className="px-3 py-1.5 rounded-xl text-[11px] font-bold border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-200 hover:bg-surface-50 dark:hover:bg-surface-800 disabled:opacity-40 flex items-center space-x-1.5"
            >
              {isResetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
              <span>Reset to original</span>
            </button>
          )}
        </div>

        {!canEdit && (
          <div className="mt-3 text-[11px] font-semibold px-3 py-2 rounded-xl bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-300 flex items-center space-x-1.5">
            <Lock className="w-3.5 h-3.5" />
            <span>You can see what each role may do, but only an administrator can change it.</span>
          </div>
        )}

        {message && (
          <div
            role="status"
            className={`mt-3 text-[11px] font-semibold px-3 py-2 rounded-xl ${
              message.error
                ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300'
                : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs border-separate border-spacing-0">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-surface-400">
                <th className="text-left font-bold py-2 pr-4 min-w-[260px]">What a role can do</th>
                {CONFIGURABLE_ROLES.map((role) => (
                  <th key={role} className="font-bold py-2 px-2 text-center w-[84px]">
                    {ROLE_LABELS[role]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map(([group, defs]) => (
                <React.Fragment key={group}>
                  <tr>
                    <td
                      colSpan={CONFIGURABLE_ROLES.length + 1}
                      className="pt-4 pb-1 text-[10px] font-extrabold uppercase tracking-wider text-brand-600 dark:text-brand-400"
                    >
                      {group}
                    </td>
                  </tr>
                  {defs.map((def) => (
                    <tr key={def.key} className="group">
                      <td className="py-2.5 pr-4 border-t border-surface-100 dark:border-surface-800 align-top">
                        <div className="font-bold text-surface-900 dark:text-surface-100">{def.label}</div>
                        <div className="text-[11px] text-surface-500 dark:text-surface-400 mt-0.5 leading-snug">
                          {def.description}
                        </div>
                      </td>
                      {CONFIGURABLE_ROLES.map((role) => {
                        const allowed = permissions[def.key]?.includes(role) ?? false;
                        const locked = isLockedCell(def.key, role);
                        const cell = `${def.key}:${role}`;
                        const busy = savingCell === cell;
                        return (
                          <td
                            key={role}
                            className="py-2.5 px-2 text-center border-t border-surface-100 dark:border-surface-800 align-middle"
                          >
                            <button
                              type="button"
                              role="checkbox"
                              aria-checked={allowed}
                              aria-label={`${ROLE_LABELS[role]}: ${def.label}`}
                              disabled={!canEdit || locked || busy}
                              onClick={() => toggle(def.key, role, !allowed)}
                              title={
                                locked
                                  ? role === 'admin'
                                    ? 'Administrators always keep every permission'
                                    : 'Kept for administrators only'
                                  : canEdit
                                  ? allowed
                                    ? `Remove from ${ROLE_LABELS[role]}`
                                    : `Allow ${ROLE_LABELS[role]}`
                                  : undefined
                              }
                              className={`w-6 h-6 rounded-lg border inline-flex items-center justify-center transition-colors ${
                                allowed
                                  ? locked
                                    ? 'bg-surface-300 dark:bg-surface-700 border-transparent text-white'
                                    : 'bg-brand-600 border-brand-600 text-white'
                                  : 'bg-white dark:bg-surface-800 border-surface-300 dark:border-surface-600 text-transparent hover:border-brand-400'
                              } ${!canEdit || locked ? 'cursor-default' : 'cursor-pointer'}`}
                            >
                              {busy ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-600" />
                              ) : allowed ? (
                                <Check className="w-3.5 h-3.5 stroke-[3]" />
                              ) : locked ? (
                                <Lock className="w-3 h-3 text-surface-400" />
                              ) : null}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RolesPermissionsView;
